/**
 * @file dashboard.tsx
 * @module app/modules/sales-management/admin/dashboard/dashboard
 * @description Implements the dashboard interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../../api/apiCallService";
import { ASSIST_ADMIN_DASHBOARD, ordersDelivery } from "../../../../../api/apiEndPoints";
import Loader from "../../../../../Global/loader";
import { OrderCancelled, OrderDelivered, OrderOutForDelivery, OrderProcessed } from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";
import { orderStatusJSON } from "../../../../../utils/staticJSON";
import DistrictWiseOrderTrend from "./chart/district-wise-order-trend";
import SalesTrend from "./chart/sales-trend";
import TargetCompletion from "./chart/target-completion";
import { listOrdersDelivery } from "../../../../../utils/storeString";
import { setKey } from "../../../../../Global/history";
import CustomDatePicker from "../../../../custom/DateRange/DatePicker";
import moment from "moment";
const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchLoader, setFetchLoader] = useState<boolean>(false);
  const [orders, setOrders] = useState<Array<any>>([]);
  const [count, setCount] = useState({
    runningRate: 0,
    salesTarget: {
      currentSalesTarget: {
        achieved: 0,
        target: 0
      }
    },
    totalActiveCustomers: 0,
    totalInactiveCustomers: 0,
    totalNewCustomers: 0,
    totalOrders: 0,
    totalSales: 0,
    totalSalesManager: 0,
    totalSalesRep: 0,
    totalActivationManager: 0,
    totalActivationRep: 0,
    totalSalesOfCurrentMonth: 0
  });
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const [startDate, setStartDate] = useState<any>(
    new Date(currentYear, currentMonth, 1)
  );
  const [endDate, setEndDate] = useState<any>(today);
  useEffect(() => {
    (async () => {
      await fetchData(startDate, endDate);
    })();
  }, []);
  const fetchData = async (start?: any, end?: any) => {
    const from = start || startDate;
    const to = end || endDate;

    let params: any = {
      needStats: true,
      needAllStats: true,
      startDate: Method.convertDateToFormat(from, "YYYY-MM-DD"),
      endDate: Method.convertDateToFormat(to, "YYYY-MM-DD"),
    };
    setLoading(true);
    let apiService = new APICallService(
      ASSIST_ADMIN_DASHBOARD,
      params,
      "",
      "",
      "",
      "",
      Dashboard
    );
    let response = await apiService.callAPI();
    if (response) {
      setCount(response);
    }
    setLoading(false);
  };
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchOrdersList();
      setFetchLoader(false);
    })();
  }, []);

  const fetchOrdersList = async () => {
    setLoading(true);
    let params: any = {
      pageNo: 1,
      limit: 10,
      sortKey: "_createdAt",
      sortOrder: -1,
    };
    let endpoint = ordersDelivery.listAssistAdmin;
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    let response: any = await apiService.callAPI();
    if (response.records) {
      setOrders(response.records);
    }
    setLoading(false);
  };
  const getStatusBadge = (status: any) => {
    const statusObj = orderStatusJSON.find((val) => val.value === status);
    if (!statusObj)
      return (
        <div className="badge rounded-pill bg-light-secondary text-secondary fs-14 p-4">
          NA
        </div>
      );

    let badgeClass = "bg-light-primary text-primary";
    switch (status) {
      case OrderProcessed:
        badgeClass = "bg-light-warning text-warning";
        break;
      case OrderOutForDelivery:
        badgeClass = "bg-light-info text-info";
        break;
      case OrderDelivered:
        badgeClass = "bg-light-success text-success";
        break;
      case OrderCancelled:
        badgeClass = "bg-light-danger text-danger";
        break;
      default:
        badgeClass = "bg-light-primary text-primary";
    }

    return (
      <div className={`badge rounded-pill ${badgeClass} fs-14 py-2 px-4`}>
        {statusObj.label}
      </div>
    );
  };
  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);

    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  };
  return (
    <div className="p-5">
      <Row className="align-items-center justify-content-between mb-7 g-5">
        <Col xl={4} md={4} sm={4} xs={4} className="fs-22 fw-bolder">
          Dashboard
        </Col>
        <Col xl={4} md={4} sm={6} xs={6} className="fs-22 fw-bolder">
          <CustomDatePicker
            className="bg-white min-h-50px fs-16 fw-bold text-dark"
            onChange={handleChange}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            dateFormat="dd/MM/yyyy"
            showFullMonthYearPicker={true}
            maxDate={today}
            inputTextBG="bg-white"
          />
        </Col>
      </Row>
      {loading ? (
        <div className="w-100 d-flex justify-content-center">
          <Loader loading={loading} />
        </div>
      ) : (
        <>
          <Row className="align-items-center mb-7 g-6">
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-ccf3f0  rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                        {count?.totalOrders > 0 ? count?.totalOrders : 0} (TSh {count?.totalSales > 0 ? Method.getGeneralizedAmount(count?.totalSales) : 0})
                    </span>
                    <span className="fw-medium fs-16">Total Orders</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            {/* <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fbe5e5 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalActiveCustomers > 0 ? count?.totalActiveCustomers : 0}
                    </span>
                    <span className="fw-medium fs-16">Active Customers</span>
                  </div>
                  <img src={IMAGES.ProductSVG} alt="" />
                </div>
              </div>
            </Col> */}
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-d4e1fc rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalNewCustomers > 0 ? count?.totalNewCustomers : 0}
                    </span>
                    <span className="fw-medium fs-16">New Customers (in last 7 days)</span>
                  </div>
                  {/* <img src={IMAGES.ProductSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            {/* <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-c6e4fb rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      TSh{" "}
                      {count?.totalSales > 0 ? Method.getGeneralizedAmount(count?.totalSales) : 0}
                    </span>
                    <span className="fw-medium fs-16">Total Sales</span>
                  </div>
                  <img src={IMAGES.SellerSVG} alt="" />
                </div>
              </div>
            </Col> */}
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-c9f3d7 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalSalesManager > 0 ? count?.totalSalesManager : 0}
                    </span>
                    <span className="fw-medium fs-16">Total Sales Manager</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fbeabc  rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalSalesRep > 0 ? count?.totalSalesRep : 0}
                    </span>
                    <span className="fw-medium fs-16">Total Sales Reps</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-e5f6de rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalActivationManager > 0 ? count?.totalActivationManager : 0}
                    </span>
                    <span className="fw-medium fs-16">Total Activation Manager</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fff4d9  rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalActivationRep > 0 ? count?.totalActivationRep : 0}
                    </span>
                      <span className="fw-medium fs-16">Total Activation Reps</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-c6e4fb rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      TSh {count?.totalSalesOfCurrentMonth > 0
                        ? Method.getGeneralizedAmount(count?.totalSalesOfCurrentMonth)
                        : 0}
                    </span>
                    <span className="fw-medium fs-16">
                      Current Month Total Sales
                    </span>
                  </div>
                  {/* <img src={IMAGES.CustomerSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fce7d4 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      TSh {count?.runningRate > 0
                        ? Method.getGeneralizedAmount(count?.runningRate)
                        : 0}
                    </span>
                    <span className="fw-medium fs-16">
                      Current Month Running Rate
                    </span>
                  </div>
                  {/* <img src={IMAGES.CustomerSVG} alt="" /> */}
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
      <Row className="align-items-center my-7 g-6">
        <SalesTrend />
        <DistrictWiseOrderTrend />
      </Row>
      <TargetCompletion />
      <Row className="align-items-center my-7 g-5">
        <Col xs>
          <div className="d-flex justify-content-between mb-5">
            <h1 className="fs-22 fw-bolder">Recent Orders</h1>
            <Button
              variant="primary"
              className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg"
              onClick={() => {
                setKey(listOrdersDelivery.currentTab, "1", false)
                navigate("/sales-management/admin/orders");
              }}
            >
              View All
            </Button>
          </div>
          {!fetchLoader ? (
            orders && orders.length ? (
              <>
                <Card className="border border-r10px">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                        <thead>
                          <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                            <th className="min-w-md-175px min-w-150px">
                              Order Date & ID
                            </th>
                            <th className="min-w-md-200px min-w-125px">
                              Customer name
                            </th>
                            <th className="min-w-md-125px">Rep Name</th>
                            <th className="min-w-md-125px min-w-100px">
                              Total amount
                            </th>
                            <th className="min-w-md-75px min-w-50px">Status</th>
                            <th className="min-w-100px text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <>
                            {orders.map((orderVal: any, goodIndex: number) => (
                              <tr key={goodIndex}>
                                <td className="fs-15 fw-500">
                                  <div className="d-flex align-items-start flex-column">
                                    <span className="text-dark d-block">
                                      {Method.convertDateToDDMMYYYYHOURS(
                                        orderVal._createdAt
                                      )}
                                    </span>
                                    <span className="text-gray d-block">
                                      {orderVal.refKey}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500 d-block">
                                    {orderVal?.customer?.name}
                                  </span>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500 badge rounded-pill bg-light-secondary text-black p-4 h-10">
                                    {orderVal?.customer?.assignedTo?.name ?? "N/A"}
                                  </span>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500">
                                    {"TSh " +
                                      Method.getGeneralizedAmount(
                                        orderVal.payment.totalCharge
                                      )}
                                  </span>
                                </td>
                                <td>{getStatusBadge(orderVal.status)}</td>
                                <td className="text-end">
                                  <Button
                                    className="uw-btn uw-btn-primary fs-14 fw-600"
                                    onClick={() => {
                                      navigate("/orders/order-details", {
                                        state: {
                                          id: orderVal._id,
                                        },
                                      });
                                    }}
                                  >
                                    View details
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </>
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </>
            ) : (
              <>
                <div className="border border-r10px mb-6">
                  <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                    <span className="fs-18 fw-500">No Data Found</span>
                  </div>
                </div>
              </>
            )
          ) : (
            <div className="border border-r10px mb-6">
              <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                <Loader loading={fetchLoader || loading} />
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
export default Dashboard;
